import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { z } from "zod";
import cookies from "js-cookie";

import Login from "../../components/Login/Login";
import SuccessSnackbar from "../../widgets/Snackbar/SuccessSnackbar";
import ErrorSnackbar from "../../widgets/Snackbar/ErrorSnackbar";
import { useAuthentication } from "../../hooks/useAuthentication";
import {
  REACT_APP_AUTHENTICATE_TOKEN_NAME,
  REACT_APP_DOMAIN,
} from "../../constants/constants";
import routes from "../../constants/routes";

const loginSchema = z.object({
  phone: z
    .string({ required_error: "Enter your phone number" })
    .min(1, "Enter your phone number")
    .regex(/^\d{10}$/, "Phone number must be exactly 10 digits"),
  password: z
    .string({ required_error: "Enter your password" })
    .min(1, "Enter your password")
    .min(6, "Password must be at least 6 characters"),
});

const initialValues = {
  phone: "",
  password: "",
};

const COOKIE_OPTIONS = {
  path: "/",
  domain: REACT_APP_DOMAIN,
  secure: process.env.NODE_ENV === "production",
  sameSite: "Strict",
};

const LoginContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mutate, isPending, isLoading } = useAuthentication();

  const isAuthenticating = isPending || isLoading;
  const [showExpiryWarning, setShowExpiryWarning] = useState(
    () => searchParams.get("sessionExpired") === "true",
  );

  const handleCloseExpiryWarning = () => {
    setShowExpiryWarning(false);
    searchParams.delete("sessionExpired");
    setSearchParams(searchParams, { replace: true });
  };

  const [successSnackbar, setSuccessSnackbar] = useState({
    open: false,
    message: "",
  });
  const [errorSnackbar, setErrorSnackbar] = useState({
    open: false,
    message: "",
  });

  const logoutSuccessMessage = location.state?.successMessage;
  const displayedSuccessMessage =
    successSnackbar.message || logoutSuccessMessage;
  const isSuccessSnackbarOpen =
    successSnackbar.open || Boolean(logoutSuccessMessage);

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbar({ open: false, message: "" });

    if (logoutSuccessMessage) {
      navigate(location.pathname, { replace: true });
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: toFormikValidationSchema(loginSchema),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      mutate(
        { mobile: values.phone, password: values.password },
        {
          onSuccess: (data) => {
            cookies.set(
              REACT_APP_AUTHENTICATE_TOKEN_NAME,
              data.accessToken,
              COOKIE_OPTIONS,
            );
            setSuccessSnackbar({
              open: true,
              message: "Login successful!",
            });
            navigate(routes.home.root, { replace: true });
          },
          onError: (error) => {
            setErrorSnackbar({
              open: true,
              message:
                error?.message ||
                "Login failed. Please check your credentials.",
            });
          },
        },
      );
    },
  });

  const {
    values,
    setFieldValue,
    handleSubmit,
    isValid,
    errors,
    touched,
    handleBlur,
    dirty,
  } = formik;

  return (
    <>
      <Login
        values={values}
        setFieldValue={setFieldValue}
        onSubmit={handleSubmit}
        isValid={isValid}
        errors={errors}
        touched={touched}
        dirty={dirty}
        handleBlur={handleBlur}
        isLoading={isAuthenticating}
        showExpiryWarning={showExpiryWarning}
        onCloseExpiryWarning={handleCloseExpiryWarning}
      />

      <SuccessSnackbar
        open={isSuccessSnackbarOpen}
        message={displayedSuccessMessage}
        onClose={handleCloseSuccessSnackbar}
      />

      <ErrorSnackbar
        open={errorSnackbar.open}
        message={errorSnackbar.message}
        onClose={() => setErrorSnackbar({ open: false, message: "" })}
      />
    </>
  );
};

export default LoginContainer;
